import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import {
  ensureMetaPixel,
  flushPendingMetaStartTrial,
  isPublicPixelPath,
  normalizePixelPath,
  trackMetaPageView,
  trackMetaViewContent,
} from "@/lib/metaPixel";

/**
 * Loads the Meta Pixel lazily on public acquisition routes only and fires
 * exactly ONE PageView per public route view (the base pixel does not
 * auto-track PageView, so there is no double-fire).
 *
 * Entirely separate from the internal LupusLogix conversion analytics: this
 * component never writes to the database and never touches `conversionTracker`.
 * Renders nothing.
 */
export const MetaPixelTracker = () => {
  const location = useLocation();
  const lastPath = useRef<string | null>(null);
  const viewContentSent = useRef(false);

  useEffect(() => {
    const path = normalizePixelPath(location.pathname);

    // Authenticated wellness-data screens (dashboard, tracking, reports,
    // settings, admin, …) are not in the allowlist — no pixel, no events.
    if (!isPublicPixelPath(path)) return;
    if (lastPath.current === path) return;
    lastPath.current = path;

    ensureMetaPixel();
    trackMetaPageView();

    // Deliver a conversion that was blocked/cut short on a previous visit.
    flushPendingMetaStartTrial();

    if (path === "/pricing" && !viewContentSent.current) {
      viewContentSent.current = true;
      trackMetaViewContent();
    }
  }, [location.pathname]);

  return null;
};

export default MetaPixelTracker;
